import { Component, computed, effect, inject, Injector, signal, ViewChild } from '@angular/core';
import { FilterChangedEvent, IServerSideDatasource, RowClickedEvent, RowModelType, ServerSideRowSelectionState, SortChangedEvent } from 'ag-grid-community';
import { $state, addSelectCustomerDialog, restManager, util } from 'app/ajs-upgraded-providers';
import { CliccableActionEInvoices, GridCellExportStyleService, GridCellFormatterService, GridClickableHistorySalesButtonComponent, GridFixedButtons, GridServerSideComponent, headersTranslate } from 'src/app/shared/components';
import { TableData } from 'src/app/shared/model/model';
import { SharedModule } from 'src/app/shared/shared.module';
import { EInvoicesStatusConfigInterface, HistoryModuleSections } from '../../../models/history.module';
import { ConfigurationManagerService, EntityManagerService, LoaderService, PaginatedResponse, RIGHT_SIDENAV_PORTAL_DATA_TOKEN, RightSidenavService, ScreenOrientationService, ToolbarEventsService } from 'src/app/core';
import { from, tap } from 'rxjs';
import { Sales } from 'tilby-models';
import { MatChipListbox, MatChipsModule } from '@angular/material/chips';
import { TranslateService } from '@ngx-translate/core';
import { EInvoiceSyncStatus, SalesEInvoicesFe } from '../../history-e-invoices.model';
import { AddSelectCustomerDialogService, AlertDialogService, EInvoiceDialogService, PrinterErrorFiscalDialogService, PromptDialogService } from 'src/app/dialogs';
import { validateUuid } from 'src/app/shared/utils';
import { HistoryUtilsService } from '../../../history-sales/services/history-utils.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mobileCheck } from 'src/utilities';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { subscribeInComponent } from '@tilby/tilby-ui-lib/utilities';
import { HistorySaleDetailComponent } from '../../../history-sales/components/history-sale-detail/history-sale-detail.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { ErrorViewerDialogService } from 'src/app/dialogs/error-viewer-dialog/error-viewer-dialog.component';
import { EInvoiceSetupDialogService } from 'src/app/dialogs/e-invoice-setup-dialog/e-invoice-setup-dialog.component';
import { OptionsDialogData, OptionsSelectorDialogService } from 'src/app/dialogs/options-selector-dialog/options-selector-dialog.component';

@Component({
  selector: 'app-history-e-invoices-showcase',
  standalone: true,
  imports: [ SharedModule, MatChipsModule ],
  templateUrl: './history-e-invoices-showcase.component.html',
  styleUrl: './history-e-invoices-showcase.component.scss'
})
export class HistoryEInvoicesShowcaseComponent {

  private readonly state = inject($state);
  private readonly loaderService = inject(LoaderService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly gridCellFormatterService = inject(GridCellFormatterService);
  private readonly restManagerService = inject(restManager);
  private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly addSelectCustomerDialogService = inject(AddSelectCustomerDialogService);
  private readonly eInvoiceDialogService = inject(EInvoiceDialogService);
  private readonly oldAddSelectCustomerDialog = inject(addSelectCustomerDialog);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly historyUtilsService = inject(HistoryUtilsService);
  private readonly optionsSelectorDialogService = inject(OptionsSelectorDialogService);
  private readonly util = inject(util);
  private readonly promptDialogService = inject(PromptDialogService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly errorViewerDialogService = inject(ErrorViewerDialogService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly rightSidenavService = inject(RightSidenavService<HistorySaleDetailComponent>);
  private readonly eInvoiceSetupDialogService = inject(EInvoiceSetupDialogService);
  private readonly gridCellExportStyleService = inject(GridCellExportStyleService);

  protected isMobile = mobileCheck();
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

  @ViewChild('eInvoicesChipList') chipList!: MatChipListbox;
  protected currentStatus = signal('error');
  
  private isSelected = signal(false); 
  private movColumnsSwitch = false;
  private isAdvancedFilterActive = false;

  public statusConfig: EInvoicesStatusConfigInterface[] = [
    { status: 'pending', 
      label: this.translateService.instant('HISTORY.E_INVOICE.FILTER_STATUS.PENDING'), 
      count: 0, 
      selected: false, 
      hidden: false
    },
    { status: 'sent', 
      label: this.translateService.instant('HISTORY.E_INVOICE.FILTER_STATUS.SENT'), 
      count: 0, 
      selected: false, 
      hidden: false
    },
    { status: 'delivered', 
      label: this.translateService.instant('HISTORY.E_INVOICE.FILTER_STATUS.DELIVERED'), 
      count: 0, 
      selected: false, 
      hidden: false 
    },
    { status: 'error', 
      label: this.translateService.instant('HISTORY.E_INVOICE.FILTER_STATUS.ERROR'), 
      count: 0, 
      selected: false, 
      hidden: false
    },
    { status: 'downloaded', 
      label: this.translateService.instant('HISTORY.E_INVOICE.FILTER_STATUS.DOWNLOADED'), 
      count: 0, 
      selected: false, 
      hidden: this.configurationManagerService.getSetting("e_invoice.send_mode") !== "download"
    },
  ];

  rowModelType: RowModelType = "serverSide";
  gridPath = this.state.$current.name.split('.').pop() === 'e-invoices';
  private isFirstLoad = true;

  protected needsSetup = false;
  protected isDownloading = false;
  protected canDownloadPendingEInvoice = this.configurationManagerService.getSetting("e_invoice.send_mode") === "download";

  public sendingEInvoices: Record<string, boolean> = {};

  @ViewChild('eInvoicesGridComponent') gridRef!: GridServerSideComponent;

  exportFormatterOptions?: (...any:any[])=>any;
  lastActiveQuery: any = null;

  fixedButtons: GridFixedButtons = {
    checkbox: {visible: true, lockPosition: 'left'},
    customButton: {
        visible: true,
        cellRenderer: GridClickableHistorySalesButtonComponent,
        lockPosition: 'right',
        cellRendererParams: {
          section: 'e_invoices' as HistoryModuleSections
        }
    }
  };

  readonly query: Readonly<any> = {
    pagination: true,
    per_page: 50,
    page: 0,
    status: 'closed',
    orderby_desc: 'closed_at',
    e_invoice: {
      invoice_progressive: 'notnull',
      status: 'error'
    }
  }

  tableData: TableData[] = [
    {
      rowData$: null,
      dataType: new SalesEInvoicesFe(),
      headersTranslate: headersTranslate.history_e_invoices_showcase,
      columnsFormatter: this.gridCellFormatterService.history_e_invoices,
      columnsExcelFormatter: this.gridCellExportStyleService.history_e_invoices,
      disabledInternalFilters: ['status'],
    }
  ];

  datasource: IServerSideDatasource = {
    getRows: (params) => {
      if(this.isFirstLoad) {
        this.loaderService.show();
        this.isFirstLoad = false;
        return;
      }

      this.gridRef.getRowsForServersideDatasource(
        params,
        [this.query],
        (a, b?) => {
          this.lastActiveQuery = { ...a };
          return from(this.entityManager.sales.fetchCollectionOnline(a) as Promise<PaginatedResponse<Sales>>).pipe(
            tap({
              next: () => this.loaderService.hide(),
              error: () => this.loaderService.hide()
            })
          );
        },
        {
          sort_type: 'desc',
          sort_col: 'closed_at'
        }
    )}
  }

  constructor() {
    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_search_bar':
          return this.resetSearchBar();
        case 'toolbar.search':
          return this.gridRef.onFilterTextBoxChanged(event.search);
        case 'grid.open_tool_panel':
          return this.gridRef.openToolPanel(event.panel);
        default:
          break;
      }
    });
  }

  resetSearchBar() {
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
  }

  ngOnInit(): void {
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
  }

  onGridReady() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      api.setGridOption('context', {
        ...api.getGridOption('context'),
        isSending: (id: string) => !!this.sendingEInvoices[id]
      });

      this.updateTable();
    })
  }

  onFilterStatusChange(newValue: string) {
    if (!newValue) {
      this.chipList.value = this.currentStatus();
      return;
    }

    this.currentStatus.set(newValue);

    this.gridRef.gridApi?.setGridOption('context', {
      ...this.gridRef.gridApi?.getGridOption('context'),
      eInvoicesStatus: newValue
    });

    // this.gridRef.gridApi?.refreshHeader();
    // this.gridRef.gridApi?.redrawRows();

    (this as any).query = {
      ...this.query,
      per_page: this.statusConfig.find(s => s.status === this.currentStatus())?.count,
      e_invoice: {
        ...this.query.e_invoice,
        status: newValue
      }
    };

    this.gridRef.gridApi?.refreshServerSide({ purge: true });
  }

  handleSelectionChange(selected: boolean) {
    this.isSelected.set(selected);
    this.createToolbarButtons(); 
  }

  onFilterChangedEmitter(event: FilterChangedEvent): void {
    this.createToolbarButtons();
    this.rightSidenavService.isOpen = false;
  }
  
  onSortChangedEmitter($event:SortChangedEvent) {
      this.rightSidenavService.isOpen = false;
  }

  private getToolbarConfig() {
    const trueSignal = signal(true);
    const selectionActive = this.isSelected(); 
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        isIt: () => this.isDownloading,
        name: 'cached',
        icon: signal('cached'),
        class: 'rotating-icon-counterclockwise',
        click: () => null
      },
      {
        isIt: () => selectionActive && !this.isDownloading,
        name: 'file_download',
        icon: signal('file_download'),
        label: signal('HISTORY.E_INVOICE.SHOWCASE.ACTIONS.DOWNLOAD_SELECTED_E_INVOICE'),
        click: () => this.downloadSelectedEInvoices()
      },
      {
        isIt: () => selectionActive,
        name: 'email',
        icon: signal('email'),
        label: signal('HISTORY.E_INVOICE.SHOWCASE.ACTIONS.SEND_EMAIL_SELECTED_E_INVOICE'),
        click: () => this.sendViaEmailSelectedEInvoices()
      }
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        isIt: () => this.canDownloadPendingEInvoice && this.currentStatus() === 'pending' && !!this.statusConfig.find(c => c.status === 'pending')?.count,
        name: 'file_download',
        icon: () => 'file_download',
        label: () => 'TOPBAR.ACTIONS.DOWNLOAD_PENDING_E_INVOICE',
        isDisable: () => this.isDownloading,
        click: () => this.downloadAllPendingEInvoices()
      },
      {
        name: 'columns_panel',
        isIt: trueSignal,
        icon: signal('view_week'),
        label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
        click: () => this.gridRef.openToolPanel('columns')
      },
      {
        isIt: trueSignal,
        name: 'movable_columns',
        icon: signal('drag_indicator'),
        label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
        click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
      },
      {
        isIt: trueSignal,
        name: 'save_columns_position',
        icon: signal('save'),
        label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
        click: () => this.gridRef.openDialogSaveColumnsPreference()
      },
      {
        isIt: () => !this.isMobile,
        name: 'advanced_filter',
        icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
        click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
      },
      {
        isIt: trueSignal,
        name: 'update',
        icon: signal('update'),
        label: signal('ITEMS.TOPBAR_ITEMS.UPDATE'),
        click: () => this.updateTable()
      },
      {
        isIt: trueSignal,
        name: 'settings_backup_restore',
        icon: signal('settings_backup_restore'),
        label: signal('TOPBAR.ACTIONS.RESET_TABLE'),
        click: () => this.gridRef.resetLayout()
      }
    ];

    return { allButtons, fixedButtons };
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  async downloadAllPendingEInvoices (): Promise<void> {
    const config: OptionsDialogData = {
      title: this.translateService.instant("DIALOG.OPTIONS_SELECTOR.DOWNLOAD_PENDING_E_INVOICE.TITLE"),
      fields: [{
        placeholder: this.translateService.instant("DIALOG.OPTIONS_SELECTOR.DOWNLOAD_PENDING_E_INVOICE.PLACEHOLDER"),
        options: [
          { name: "Non scaricare", value: null },
          { name: "Scarica e contrassegna come inviate", value: "sent"} ,
          { name: "Scarica senza contrassegnare come inviate", value: "pending" }
        ]
      }]
    }

    const results: any = await this.optionsSelectorDialogService.openDialog({data: config});

    const option = results?.[0];

    if (option && option !== false) {
      this.isDownloading = true;
      
      try {
        let config: any;

        switch (option) {
          case 'pending':
            config = { status: "pending" };
            break;
          case 'sent':
            config = { set_as: "sent", status: "pending" };
            break;
        }

        if (config) {
          const response = await this.restManagerService.downloadOne(
            "sales/e_invoices/download", 
            "", 
            config
          );

          const blobData = response.data || response;
          this.util.downloadFile(blobData, 'fatture.zip', 'application/zip');

          await this.getEInvoicesCounts();
        }
      } catch (error) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.DOWNLOAD_E_INVOICE_ERROR")
          }
        });
      } finally {
        this.isDownloading = false;
      }
    }
  }

  async downloadSelectedEInvoices(): Promise<void> {
    const selectedRows = this.getSelectedIds();

    if (selectedRows.length === 0) {
      return;
    }

    const ids = selectedRows.join(',');
    this.isDownloading = true;

    try {
      const response = await this.restManagerService.downloadOne(
        "sales/e_invoices/download", 
        "", 
        { ids: ids }
      );

      const blobData = response.data || response;
      this.util.downloadFile(blobData, 'fatture.zip', 'application/zip');

    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.DOWNLOAD_SELECTED_E_INVOICE_ERROR")
        }
      });
    } finally {
      this.isDownloading = false;
    }
  }

  getSelectedIds(): string[] {
    const api = this.gridRef.gridApi;
    if (!api) return [];

    const state = api.getState().rowSelection as ServerSideRowSelectionState;

    // CASO 1: Selezione Manuale
    if (!state?.selectAll) {
      return api.getSelectedRows().map(sale => String(sale.id));
    } 

    // CASO 2: "Seleziona Tutto" 
    const ids: string[] = [];
    api.forEachNode((node) => {
      if (node.isSelected() && node.data?.id) {
        ids.push(String(node.data.id));
      }
    });

    return ids;
  }

  async sendViaEmailSelectedEInvoices(): Promise<void> {
    const selectedRows = this.getSelectedIds();

    if (selectedRows.length === 0) {
      return;
    }

    const ids = selectedRows.join(',');
    const defaultEmail = '';

    try {
      const emailAddress = await this.promptDialogService.openDialog({
        data: { 
          title: this.translateService.instant("DIALOG.PROMPT.TITLE_EMAIL_E_INVOICES"), 
          label: "Email", 
          type: "email", 
          value: defaultEmail 
        }
      });

      if (emailAddress) {
        await this.restManagerService.downloadOne(
          "sales/e_invoices/export/email",
          "", 
          { ids: ids, email: emailAddress }
        );

        this._snackBar.open(
          this.translateService.instant('HISTORY.E_INVOICE.MESSAGES.SEND_VIA_EMAIL_E_INVOICE_SUCCESS'), 
          undefined, 
          { 
            duration: 3000, 
            horizontalPosition: 'start', 
            verticalPosition: "bottom", 
          }
        );
      }
    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.SEND_VIA_EMAIL_E_INVOICES_ERROR")
        }
      });
    }
  }

  clearGridFilters() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      const model = this.gridRef.gridApi?.getFilterModel();

      if(model) {
        for (const colId of Object.keys(model)) {
          const filterInstance = await api.getColumnFilterInstance(colId);
          
          if (filterInstance) {
            filterInstance.setModel(null);
          }
        }
      }

      api.onFilterChanged();
    })
  }

  async updateTable() {
    await this.getEInvoicesCounts();
    this.onFilterStatusChange(this.currentStatus());
  }

  async getEInvoicesCounts(): Promise<void> {
    try {
      const result: EInvoiceSyncStatus = await this.restManagerService.getOne('sales/e_invoices', 'sync_status');
      this.needsSetup = false;

      this.statusConfig.forEach(config => {
        config.count = result?.status?.[config.status] ?? 0;
      });

    } catch (error: any) {
      if (error?.data?.error === 'SETUP_NEEDED') {
        this.needsSetup = true;
      }
    }
  }

  async startSetup(): Promise<void> {
    try {
      const result = await this.eInvoiceSetupDialogService.openDialog();
      if(result) {
        await this.getEInvoicesCounts();

        let resultMsg = 'ONBOARDING_COMPLETE';
        
        if (result && typeof result === 'object') {
          switch (result.contract_status) {
            case 'SENT':
              resultMsg = 'NEEDS_CONTRACT_SIGN';
              break;
            case 'NOT_SENT':
              resultMsg = 'CONTRACT_SEND_ERROR';
              break;
          }
        }
  
        this.gridRef.gridApi?.refreshServerSide({ purge: true });
  
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant('HISTORY.E_INVOICE_SETUP.' + resultMsg)
          }
        });
      }

    } catch (error) {
      if (typeof error === 'string') {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant('HISTORY.E_INVOICE_SETUP.' + error)
          }
        });
      }
    }
  }

  rowClicked({data, node}: RowClickedEvent) {
    this.openDetail(data);
  }

  openDetail(data: Sales|undefined) {
    const portalInjector = Injector.create({
      providers: [{
        provide: RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
        useValue: {sale: data, gridRef: this.gridRef}
      }],
    });
    this.rightSidenavService.componentPortal$.next(new ComponentPortal(HistorySaleDetailComponent, null, portalInjector));
    this.rightSidenavService.isOpen = true;
  }

  actions({ action, data }: CliccableActionEInvoices<Sales>) {
    if(data) {
      switch (action) {
        case "send-e-invoice": 
          return this.sendEInvoice(data);
        case "edit-customer": 
          return this.editCustomer(data);
        case "edit-invoice-options": 
          return this.editInvoiceOptions(data);
        case "duplicate-sale": 
          return this.duplicateSale(data);
        case "change-sale-status": 
          return this.changeSaleStatus(data);
        case "download-e-invoice": 
          return this.downloadEInvoice(data);
        case "send-via-email-e-invoice": 
          return this.sendViaEmailEInvoice(data);
        case "open-help-message": 
          return this.openHelpMessage(data);
      }
    }
  }

  async sendEInvoice(sale: Sales): Promise<void> {
    if (!sale.id) return;
    this.sendingEInvoices[sale.id] = true;

    try {
      let options: any = {};

      const previousSaleDocument = sale.sale_documents?.find((saleDoc) => 
        ['e_invoice', 'summary_e_nrc', 'summary_e_rc'].includes(saleDoc.document_type)
      );

      if (previousSaleDocument) {
        options = {
          printer_id: previousSaleDocument.printer_id,
          document_type: previousSaleDocument.document_type
        };

        if (previousSaleDocument.meta) {
          const { print_ddt, print_only_departments, save_paper } = previousSaleDocument.meta;
          Object.assign(options, { print_ddt, print_only_departments, save_paper });
        }
      }

      await this.restManagerService.getOne("send_e_invoice", sale.id, options);

      this.updateTable();

    } catch (err) {
      const errorCode = this.getErrorCode(err);
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.printerErrorFiscalService.translateError(errorCode)
        }
      });
    } finally {
      delete this.sendingEInvoices[sale.id];
    }
  }

  private getErrorCode(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.status === -1) {
      return 'E_INVOICE_OFFLINE';
    }

    if (error?.data?.error_code) {
      return error.data.error_code;
    }

    return 'UNKNOWN_ERROR';
  }

  async editCustomer(sale: Sales): Promise<void> {
    const customer = structuredClone(sale.sale_customer);

    const saleCustomer: any = this.configurationManagerService.getPreference('cashregister.use_new_add_customer_dialog')
        ? await this.addSelectCustomerDialogService.openDialog({ data: {currentCustomer: customer} }).then(data => data?.sale_customer)
        : await this.oldAddSelectCustomerDialog.show(customer);

    if (!saleCustomer) return;

    const saleCustomerId = String(saleCustomer?.id);

    if (validateUuid(saleCustomerId)) {
        saleCustomer.uuid = saleCustomerId;
        saleCustomer.customer_id = null;
    } else {
        saleCustomer.customer_id = saleCustomerId;
    }

    delete saleCustomer.id;
    sale.sale_customer = saleCustomer;

    try {
        await this.updateSale(sale);
        this.updateTable();
    } catch (err) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.EDIT_CUSTOMER_ERROR")
        }
      })
    }
  };

  async updateSale (sale: Sales): Promise<void> {
    const updatedSale = await this.restManagerService.post("sales", sale, { force_update: true });
    Object.assign(sale, updatedSale);
  }

  async editInvoiceOptions (sale: Sales): Promise<void> {
    const eInvoice = structuredClone(sale.e_invoice ?? {});

    const eInvoiceOptions = await this.eInvoiceDialogService.openDialog({ 
      data: { e_invoice: eInvoice } 
    });

    if (!eInvoiceOptions) {
      return;
    }

    sale.e_invoice = {
      ...sale.e_invoice,
      ...eInvoiceOptions
    };

    try {
      await this.updateSale(sale);

      this._snackBar.open(this.translateService.instant('HISTORY.E_INVOICE.MESSAGES.EDIT_E_INVOICE_OPTIONS_SUCCESS'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (err) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.INVOICE_OPTIONS_ERROR")
        }
      })
    }
  }

  duplicateSale (sale: Sales): void {
    if (this.configurationManagerService.isModuleEnabled('cashregister')) {
      const duplicatedSale = structuredClone(sale);

      const isCustomerEmpty = !sale.sale_customer || Object.keys(sale.sale_customer).length === 0;
      const saleCustomer = isCustomerEmpty 
        ? sale.customer_tax_code 
        : structuredClone(sale.sale_customer);

      this.historyUtilsService.createSale(
        {
          e_invoice: duplicatedSale.e_invoice,
          name: duplicatedSale.name,
          price_changes: duplicatedSale.price_changes,
          sale_customer: saleCustomer,
          sale_items: duplicatedSale.sale_items
        }, 
        {
          saleType: 'e_invoice'
        }
      )

    } else {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.DUPLICATE_SALE_ERROR")
        }
      })
    }
  }

  async changeSaleStatus (sale: Sales): Promise<void> {
    const config: OptionsDialogData = {
      title: this.translateService.instant('DIALOG.OPTIONS_SELECTOR.CHANGE_SALE_STATUS.TITLE', { value: sale.e_invoice?.invoice_yearly_progressive }),
      fields: [{
        placeholder: this.translateService.instant('DIALOG.OPTIONS_SELECTOR.CHANGE_SALE_STATUS.PLACEHOLDER'),
        options: [
          { name: "Da Inviare", value: "pending" },
          { name: "Inviata", value: "sent" },
          { name: "Consegnata", value: "delivered" },
          { name: "Scaricata", value: "downloaded" }
        ]
      }]
    }

    const results: any = await this.optionsSelectorDialogService.openDialog({data: config});
    console.log("results ", results);

    const option = results?.[0];

    if (option && option !== false) {
      const clonedSale = structuredClone(sale);
      
      if (clonedSale.e_invoice) {
        clonedSale.e_invoice.status = option;
      }

      try {
        if(clonedSale.id)
          this.sendingEInvoices[clonedSale.id] = true;

        await this.restManagerService.post("sales", clonedSale, { force_update: true });

        // await new Promise(resolve => setTimeout(resolve, 10000));

        this._snackBar.open(this.translateService.instant('HISTORY.E_INVOICE.MESSAGES.CHANGE_SALE_STATUS_SUCCESS'), undefined, { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        });
        this.updateTable();

      } catch (error) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.CHANGE_SALE_STATUS_ERROR")
          }
        })
      } finally {
        if(clonedSale.id)
          delete this.sendingEInvoices[clonedSale.id];
      }
    }
  }

  async downloadEInvoice(sale: Sales): Promise<void> {
    this.isDownloading = true;

    try {
      const response = await this.restManagerService.downloadOne("sales/e_invoices/download", sale.id);

      let filename = ''; //`fattura_${sale.id}.xml`
        
      const headers = typeof response.headers === 'function' ? response.headers() : response.headers;
      const disposition = headers?.['content-disposition'];

      if (disposition && disposition.includes('attachment')) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        
        if (matches?.[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      this.util.downloadFile(response.data, filename, 'application/xml');
    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.DOWNLOAD_E_INVOICE_ERROR")
        }
      })
    } finally {
      this.isDownloading = false;
    }
  }

  async sendViaEmailEInvoice (sale: Sales): Promise<void> {
    const defaultEmail = '';

    const emailAddress = await this.promptDialogService.openDialog({
      data: {          
        title: this.translateService.instant("DIALOG.PROMPT.TITLE_EMAIL_E_INVOICE"), 
        label: "Email", 
        type: "email", 
        value: defaultEmail 
      } 
    });

    if (emailAddress) {
      try {
        await this.restManagerService.downloadOne(
          "sales/e_invoices/export/email",
          "", 
          { ids: sale.id, email: emailAddress }
        );
        this._snackBar.open(this.translateService.instant('HISTORY.E_INVOICE.MESSAGES.SEND_VIA_EMAIL_E_INVOICE_SUCCESS'), undefined, { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        });
      } catch (error) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant("HISTORY.E_INVOICE.MESSAGES.SEND_VIA_EMAIL_E_INVOICE_ERROR")
          }
        })
      }
    }
  }

  async openHelpMessage (sale: Sales): Promise<void> {
    return this.errorViewerDialogService.openDialog({
      data: {
        title: this.translateService.instant("DIALOG.ERROR_VIEWER.E_INVOICE_TITLE"),
        message: sale.e_invoice?.external_message || ''
      }
    })
  }
}
